import { Loader2 } from "lucide-react";
import { motion } from "framer-motion";
import { useGoogleLogin } from "@react-oauth/google";
import { Button } from "@/components/ui/button";
import { ENV } from "@/config/env";
import { useGoogleLoginMutation } from "../hooks/useAuth";
import { LoginHeader } from "./components";

export default function EmployeeLogin() {
  const googleLoginMutation = useGoogleLoginMutation();

  const handleGoogleLogin = useGoogleLogin({
    onSuccess: (tokenResponse) => {
      googleLoginMutation.mutate({
        access_token: tokenResponse.access_token,
      });
    },
    onError: (error) => {
      console.error("Google SSO Login Failed:", error);
    },
  });

  const isLoading = googleLoginMutation.isPending;

  return (
    <div className="min-h-screen w-full flex flex-col bg-gradient-to-br from-amber-100 via-fuchsia-50 to-purple-200 dark:from-[#261705] dark:via-[#1a0f2e] dark:to-[#0a071f] overflow-hidden relative">
      {/* Base Background Image with reduced opacity and blend mode to match theme */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat pointer-events-none opacity-30 dark:opacity-20 mix-blend-multiply dark:mix-blend-screen"
        style={{ backgroundImage: "url('/bg.png')" }}
      />

      {/* Theme Color Gradient Overlay to enrich colors further */}
      <div className="absolute inset-0 bg-gradient-to-br from-amber-500/20 via-fuchsia-600/20 to-purple-800/30 pointer-events-none" />

      {/* Decorative gradient orbs for immersive brand experience */}
      <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-amber-400/30 dark:bg-amber-500/10 rounded-full blur-[100px] pointer-events-none -translate-x-1/2 -translate-y-1/2" />
      <div className="absolute bottom-0 right-0 w-[800px] h-[800px] bg-purple-500/30 dark:bg-purple-600/10 rounded-full blur-[120px] pointer-events-none translate-x-1/3 translate-y-1/3" />

      {/* Content Layer */}
      <div className="flex-1 flex flex-col relative z-10 w-full max-w-2xl mx-auto lg:max-w-none">
        {/* Top Header (Lang & Theme) */}
        <div className="w-full flex justify-end p-6 xl:p-8">
          <LoginHeader />
        </div>

        {/* Center Form Content */}
        <div className="flex-1 flex flex-col justify-center items-center p-6 sm:p-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="w-full max-w-[480px] bg-white dark:bg-slate-900 rounded-3xl p-8 lg:p-10 shadow-2xl text-slate-900 dark:text-white border border-slate-200/80 dark:border-slate-800 relative"
          >
            <div className="flex flex-col items-center mb-8 pt-4">
              <img
                src={ENV.COMPANY_LOGO}
                alt="DRC Systems"
                className="h-12 object-contain mb-10"
              />
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
                Employee Login
              </h1>
              <p className="text-slate-500 dark:text-slate-400 text-sm mt-1 text-center">
                Sign in using your corporate Google account
              </p>
            </div>

            <div className="space-y-4 py-4">
              <Button
                onClick={() => handleGoogleLogin()}
                disabled={isLoading}
                variant="outline"
                className="w-full h-13 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 hover:border-purple-300 text-slate-800 dark:text-slate-100 font-semibold text-base shadow-sm hover:shadow-md transition-all flex items-center justify-center py-6"
              >
                {isLoading ? (
                  <Loader2 className="mr-3 h-5 w-5 animate-spin text-purple-700 dark:text-purple-400" />
                ) : (
                  <svg className="mr-3 h-6 w-6" viewBox="0 0 24 24">
                    <path
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      fill="#4285F4"
                    />
                    <path
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      fill="#34A853"
                    />
                    <path
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      fill="#FBBC05"
                    />
                    <path
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      fill="#EA4335"
                    />
                  </svg>
                )}
                {isLoading ? "Authenticating..." : "Sign in with Google"}
              </Button>

              {googleLoginMutation.isError && (
                <div className="p-3 text-xs font-medium text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/40 rounded-xl border border-red-200 dark:border-red-800 text-center">
                  {googleLoginMutation.error?.message ||
                    "Google authentication failed. Please try again."}
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
